import type { FieldErrorItem } from '@forgd/ui-kit/blocks/field'
import type { AnyFieldApi } from '@tanstack/vue-form'

export interface BindableField<TValue> {
  name: string
  form: { formId: string, state: { submissionAttempts: number } }
  state: { value: TValue, meta: { errors: unknown[], isDirty: boolean, isBlurred: boolean } }
  handleChange: (value: TValue) => void
  handleBlur: () => void
  setMeta: AnyFieldApi['setMeta']
}

export interface BoundField<TValue> {
  label: { for: string }
  control: {
    'id': string
    'modelValue': TValue
    'error': boolean
    'aria-describedby': string | undefined
    'onUpdate:modelValue': (value: unknown) => void
    'onBlur': () => void
    'onFocus': () => void
  }
  error: { id: string, errors: FieldErrorItem[] }
}

export interface BoundNumberField<TValue> {
  label: BoundField<TValue>['label']
  root: Pick<BoundField<TValue>['control'], 'modelValue' | 'onUpdate:modelValue'>
  group: Pick<BoundField<TValue>['control'], 'error'>
  input: Pick<BoundField<TValue>['control'], 'id' | 'aria-describedby' | 'onBlur' | 'onFocus'>
  error: BoundField<TValue>['error']
}

export interface RevealableForm {
  validate: (cause: 'submit') => unknown
}

/**
 * Re-runs validation so every field with an error re-renders and `bindField` shows it.
 * Pass it as the form's `onSubmitInvalid`: the first invalid submit skips validation,
 * so fields the user never changed would otherwise keep their errors hidden.
 */
export function revealErrors(form: RevealableForm): void {
  form.validate('submit')
}

const FOCUSABLE = 'input, button, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'

/**
 * Scrolls the first invalid control inside `root` into view and focuses it.
 * Call it after `revealErrors` and a `nextTick`, so the invalid controls are rendered.
 */
export function focusFirstInvalid(root: HTMLElement | null | undefined): void {
  const invalid = root?.querySelector<HTMLElement>('[aria-invalid="true"]')
  if (!invalid) {
    return
  }
  const target = invalid.matches(FOCUSABLE) ? invalid : invalid.querySelector<HTMLElement>(FOCUSABLE)
  invalid.scrollIntoView({ block: 'center', behavior: 'smooth' })
  target?.focus({ preventScroll: true })
}

/**
 * A form-level schema validates every field on each change, so a field shows its errors
 * only once the user has changed and left it, or has tried to submit.
 */
export function isFieldReached(meta: { isDirty: boolean, isBlurred: boolean } | undefined, submissionAttempts: number): boolean {
  return Boolean(meta?.isDirty && meta.isBlurred) || submissionAttempts > 0
}

function toErrorItem(error: unknown): FieldErrorItem {
  if (typeof error === 'string') {
    return error
  }
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string' && error.message) {
    return { message: error.message }
  }
  return null
}

/**
 * Maps a TanStack Form field onto the props of the ui-kit Field parts:
 * `label` → FieldLabel, `control` → the input, `error` → FieldError.
 */
export function bindField<TValue>(field: BindableField<TValue>): BoundField<TValue> {
  const id = `${field.form.formId}-${field.name}`
  const errorId = `${id}-error`
  // A field that shows no error when focused waits for blur again, while one in error updates live.
  const reached = isFieldReached(field.state.meta, field.form.state.submissionAttempts)
  const visibleErrors = reached ? field.state.meta.errors : []
  const errors = visibleErrors.map(toErrorItem)
  // Any error marks the field invalid, even one without a message to show.
  const invalid = visibleErrors.some(Boolean)

  return {
    label: { for: id },
    control: {
      'id': id,
      'modelValue': field.state.value,
      'error': invalid,
      'aria-describedby': errors.some(Boolean) ? errorId : undefined,
      // Controls type their emits more broadly than the field (Input emits string | number | null).
      'onUpdate:modelValue': value => field.handleChange(value as TValue),
      'onBlur': () => field.handleBlur(),
      'onFocus': () => {
        if (!invalid) {
          field.setMeta(prev => ({ ...prev, isBlurred: false }))
        }
      },
    },
    error: { id: errorId, errors },
  }
}

/**
 * `bindField` for a NumberField inside an InputGroup: the value goes on NumberField,
 * the error state on InputGroup, and the id and focus handlers on NumberFieldInput.
 */
export function bindNumberField<TValue>(field: BindableField<TValue>): BoundNumberField<TValue> {
  const { label, control, error } = bindField(field)
  return {
    label,
    root: { 'modelValue': control.modelValue, 'onUpdate:modelValue': control['onUpdate:modelValue'] },
    group: { error: control.error },
    input: { 'id': control.id, 'aria-describedby': control['aria-describedby'], 'onBlur': control.onBlur, 'onFocus': control.onFocus },
    error,
  }
}
